/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import { CircleSlash, Edit2, Plus, Trash2 } from "lucide-react";
import { Input, MultiSelect, Select } from "../component";
import { Metadata } from "../../../../interfaces/metadata";
import { AdvancedOptionsProps, SelectValue } from "../interfaces";

export const AdvancedOptions: React.FC<AdvancedOptionsProps> = (props) => {
  const {
    disabled,
    entityMetadata,
    excludeKeys = [],
    formStatus,
    metadata,
    saveMetadata,
    multiSelectKeys = [],
  } = props;
  const [currentMetadata, setCurrentMetadata] = useState<SelectValue>({
    label: "",
    value: "",
  });
  const [editingMetadataKey, setEditingMetadataKey] = useState<string>("");
  const [selectedMetadataValue, setSelectedMetadataValue] =
    useState<string>("");
  const [selectedMetadataOption, setSelectedMetadataOption] =
    useState<SelectValue>({
      label: "",
      value: "",
    });
  const [selectedMetadataOptions, setSelectedMetadataOptions] = useState<
    SelectValue[]
  >([]);

  const filteredEntityMetadata: Metadata[] = useMemo(() => {
    if (excludeKeys.length === 0) {
      return entityMetadata;
    }
    return entityMetadata.filter(
      (option: Metadata) => !excludeKeys.includes(option.key),
    );
  }, [entityMetadata, excludeKeys]);

  const currentMetadataInfo: Metadata | undefined = useMemo(() => {
    return filteredEntityMetadata.find(
      (option) => option.key === currentMetadata.value,
    );
  }, [currentMetadata, filteredEntityMetadata]);

  const isCurrentMultiSelect: boolean = useMemo(() => {
    return (
      !!currentMetadataInfo &&
      multiSelectKeys.includes(currentMetadataInfo.key) &&
      !!currentMetadataInfo.options &&
      currentMetadataInfo.options.length > 0
    );
  }, [currentMetadataInfo, multiSelectKeys]);

  const visibleMetadataKeys: string[] = useMemo(() => {
    return Object.keys(metadata).filter((key) =>
      filteredEntityMetadata.some((option) => option.key === key),
    );
  }, [filteredEntityMetadata, metadata]);

  const resetMetadataInputs = (): void => {
    setCurrentMetadata({
      label: "",
      value: "",
    });
    setSelectedMetadataOption({
      label: "",
      value: "",
    });
    setSelectedMetadataValue("");
    setSelectedMetadataOptions([]);
    setEditingMetadataKey("");
  };

  const saveNewMetadata = (metadata: Record<string, any>): void => {
    saveMetadata(metadata);
    resetMetadataInputs();
  };

  const addMetadata = (): void => {
    if (currentMetadata.value === "") return;

    if (isCurrentMultiSelect) {
      if (selectedMetadataOptions.length === 0) return;
      const newMetadata: Record<string, any> = {
        ...metadata,
        [currentMetadata.value]: selectedMetadataOptions
          .map((option) => option.value)
          .join(","),
      };
      saveNewMetadata(newMetadata);
      return;
    }

    if (
      currentMetadataInfo?.data_type === "string" &&
      selectedMetadataValue.trim() === ""
    )
      return;

    if (currentMetadataInfo?.data_type === "boolean") {
      if (selectedMetadataOption.value === "") return;

      const newMetadata: Record<string, any> = {
        ...metadata,
        [currentMetadata.value]: Boolean(
          parseInt(selectedMetadataOption.value),
        ),
      };

      saveNewMetadata(newMetadata);
      return;
    }

    if (
      currentMetadataInfo?.options &&
      currentMetadataInfo.options.length > 0
    ) {
      if (selectedMetadataOption.value === "") return;

      const newMetadata: Record<string, any> = {
        ...metadata,
        [currentMetadata.value]:
          currentMetadataInfo?.data_type === "integer"
            ? parseInt(selectedMetadataOption.value)
            : selectedMetadataOption.value,
      };

      saveNewMetadata(newMetadata);
      return;
    }

    const newMetadataValue =
      currentMetadataInfo?.data_type === "integer"
        ? parseInt(selectedMetadataValue || "0")
        : currentMetadataInfo?.data_type === "float"
          ? parseFloat(selectedMetadataValue || "0")
          : selectedMetadataValue;

    const newMetadata: Record<string, any> = {
      ...metadata,
      [currentMetadata.value]: newMetadataValue,
    };

    saveNewMetadata(newMetadata);
  };

  const editMetadata = (key: string): void => {
    const metadataInfo = filteredEntityMetadata.find(
      (option) => option.key === key,
    );
    const metadataValue = metadata[key];
    const metadataOption = metadataInfo?.options?.find(
      (option) => option.value === parseInt(metadataValue),
    );

    setCurrentMetadata({
      label: metadataInfo?.label || "",
      value: key,
    });
    setEditingMetadataKey(key);

    const isMulti: boolean =
      multiSelectKeys.includes(key) &&
      !!metadataInfo?.options &&
      metadataInfo.options.length > 0;

    if (isMulti) {
      const csv: string = String(metadataValue || "");
      const codes: string[] = csv === ""
        ? []
        : csv.split(",").map((code) => code.trim()).filter(Boolean);
      const selected: SelectValue[] = (metadataInfo?.options || [])
        .filter((option) => codes.includes(String(option.value)))
        .map((option) => ({
          label: option.label,
          value: String(option.value),
        }));
      setSelectedMetadataOptions(selected);
      return;
    }

    if (metadataInfo?.data_type === "boolean") {
      setSelectedMetadataOption({
        label: metadataValue ? "Sí" : "No",
        value: metadataValue ? "1" : "0",
      });
      return;
    }

    if (metadataInfo?.options && metadataInfo.options.length > 0) {
      setSelectedMetadataOption({
        label: metadataOption?.label || "",
        value: String(metadataValue),
      });
      return;
    }

    setSelectedMetadataValue(metadataValue);
  };

  const deleteMetadata = (key: string): void => {
    const newMetadata: Record<string, any> = {
      ...metadata,
    };

    delete newMetadata[key];

    saveMetadata(newMetadata);

    if (key === editingMetadataKey) resetMetadataInputs();
  };

  const parseMetadataValue = (key: string) => {
    const metadataInfo = filteredEntityMetadata.find(
      (option) => option.key === key,
    );
    const metadataValue = metadata[key];

    if (metadataInfo?.data_type === "boolean")
      return metadataValue ? "Sí" : "No";

    const isMulti: boolean =
      multiSelectKeys.includes(key) &&
      !!metadataInfo?.options &&
      metadataInfo.options.length > 0;

    if (isMulti) {
      const csv: string = String(metadataValue || "");
      if (csv === "") return "";
      const codes: string[] = csv.split(",").map((code) => code.trim());
      return (metadataInfo?.options || [])
        .filter((option) => codes.includes(String(option.value)))
        .map((option) => option.label)
        .join(", ");
    }

    if (metadataInfo?.options && metadataInfo.options.length > 0)
      return (
        metadataInfo.options.find((option) => option.value === metadataValue)
          ?.label || metadataValue
      );

    return metadataInfo?.data_type === "string"
      ? metadataValue
      : Number(metadataValue || 0).toLocaleString();
  };

  useEffect(() => {
    if (formStatus === "hidden" || formStatus === "detail")
      resetMetadataInputs();
  }, [formStatus]);

  return (
    <>
      <div className="flex flex-col justify-between gap-4 sm:flex-row">
        <Select
          label="Metadato"
          placeholder="Selecciona un metadato"
          disabled={disabled}
          items={filteredEntityMetadata
            .filter(
              (option) =>
                !Object.keys(metadata).some((key) => key === option.key),
            )
            .map((option) => ({
              name: option.label,
              path: option.key,
            }))}
          value={currentMetadata.value ? currentMetadata : null}
          onChange={(option) => {
            setCurrentMetadata(option);
            setSelectedMetadataValue("");
            setSelectedMetadataOption({
              label: "",
              value: "",
            });
          }}
        />
        <div className="flex w-full gap-4">
          {isCurrentMultiSelect ? (
            <MultiSelect
              label="Valor"
              placeholder="Selecciona opciones"
              disabled={disabled}
              items={(currentMetadataInfo?.options || []).map((option) => ({
                name: option.label,
                path: String(option.value),
              }))}
              value={selectedMetadataOptions}
              onChange={(values) =>
                setSelectedMetadataOptions(values as SelectValue[])
              }
            />
          ) : currentMetadataInfo?.data_type === "boolean" ? (
            <Select
              label="Valor"
              placeholder="Selecciona una opción"
              disabled={disabled}
              items={[
                { name: "Sí", path: "1" },
                { name: "No", path: "0" },
              ]}
              value={
                selectedMetadataOption.value ? selectedMetadataOption : null
              }
              onChange={(value) => setSelectedMetadataOption(value)}
            />
          ) : currentMetadataInfo?.options &&
            currentMetadataInfo.options.length > 0 ? (
            <Select
              label="Valor"
              placeholder="Selecciona una opción"
              disabled={disabled}
              items={currentMetadataInfo.options.map((option) => ({
                name: option.label,
                path: option.value.toString(),
              }))}
              value={
                selectedMetadataOption.value ? selectedMetadataOption : null
              }
              onChange={(value) => setSelectedMetadataOption(value)}
            />
          ) : currentMetadataInfo?.data_type === "string" ? (
            <Input
              type="text"
              label="Valor"
              placeholder="Valor del metadato"
              disabled={disabled}
              value={selectedMetadataValue}
              onChange={(value) => setSelectedMetadataValue(value)}
            />
          ) : (
            <Input
              type="number"
              label="Valor"
              placeholder="Valor del metadato"
              disabled={disabled}
              value={selectedMetadataValue}
              onChange={(value) => {
                if (currentMetadataInfo?.data_type === "integer")
                  setSelectedMetadataValue(value.replace(/[^0-9]/g, ""));
                else if (currentMetadataInfo?.data_type === "float")
                  setSelectedMetadataValue(value.replace(/[^0-9.]/g, ""));
                else setSelectedMetadataValue(value);
              }}
            />
          )}
          <button
            className={`mt-auto flex h-10 w-10 shrink-0 items-center justify-center rounded-md transition-colors duration-300 ${
              editingMetadataKey
                ? "bg-black text-white hover:bg-gray-800"
                : "border border-gray-200 bg-white text-gray-500 hover:bg-gray-100 disabled:bg-gray-300/20"
            }`}
            disabled={disabled}
            onClick={addMetadata}
          >
            {editingMetadataKey ? (
              <Edit2 className="h-4 w-4 shrink-0" />
            ) : (
              <Plus className="h-4 w-4 shrink-0" />
            )}
          </button>
        </div>
      </div>
      <div className="mt-4 overflow-hidden rounded-md border border-gray-200 bg-white">
        <div className="h-[337px] overflow-y-auto">
          {visibleMetadataKeys.length === 0 ? (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <CircleSlash
                  className="mx-auto mb-3 h-12 w-12 text-gray-300"
                  strokeWidth={1.5}
                />
                <p className="text-sm text-gray-500">
                  No hay metadatos agregados
                </p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {visibleMetadataKeys.map((key) => (
                <div
                  key={key}
                  className="flex items-center justify-between gap-4 px-4 py-3"
                >
                  <div className="w-[47%] min-w-0 sm:w-[52%]">
                    <label className="block truncate text-xs text-gray-800 sm:text-sm">
                      {
                        filteredEntityMetadata.find(
                          (option) => option.key === key,
                        )?.label
                      }
                    </label>
                  </div>
                  <div className="flex w-[53%] min-w-0 items-center justify-end gap-3 sm:w-[48%] sm:justify-between">
                    <label
                      className="min-w-0 flex-1 truncate text-xs text-gray-600 sm:text-sm"
                      title={String(parseMetadataValue(key))}
                    >
                      {parseMetadataValue(key)}
                    </label>
                    <div className="flex shrink-0 gap-1">
                      <button
                        className={`rounded-md p-2 transition-colors hover:bg-gray-100 ${
                          disabled ? "opacity-0" : "opacity-100"
                        }`}
                        disabled={disabled}
                        onClick={() => editMetadata(key)}
                      >
                        <Edit2 className="h-4 w-4 text-gray-500" />
                      </button>
                      <button
                        className={`rounded-md p-2 transition-colors hover:bg-gray-100 ${
                          disabled ? "opacity-0" : "opacity-100"
                        }`}
                        disabled={disabled}
                        onClick={() => deleteMetadata(key)}
                      >
                        <Trash2 className="h-4 w-4 text-gray-500" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
